/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Center, Group, Switch, Text } from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { FC, useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import { useForm } from '@mantine/form';
import { GoogleCalendarDto } from '../../../../Types/GoogleT';
import Dot from '../../../../Atoms/Dot';
import ConfigOption from '../../Google/ConfigOption';
import PanePaper from '../../../../Molecules/PanePaper';
import { GoogleS } from '../../../../Service/GoogleS';
import { LicenceRestriction, LicenceS } from '../../../../Service/LicenceS';
import { MixpanelS } from '../../../../Service/MixpanelS';
import { ConstantS } from '../../../../Service/ConstantS';
import GrayBox from '../../../../Atoms/GrayBox';

// eslint-disable-next-line import/prefer-default-export
export const openGoogleCalendarConfigModal = (
  googleCalendar: GoogleCalendarDto,
  updateHandler: (patch: Partial<GoogleCalendarDto>) => void,
  checkLicence: (licenceRestriction: LicenceRestriction, featureHandler: () => unknown) => void,
) => {
  openModal({
    size: 'lg',
    title: (
      <Text weight="bolder" size="xl">
        🗓️ Kalenderoptionen
      </Text>
    ),
    closeOnEscape: true,
    transition: 'slide-down',
    children: (
      <GoogleCalendarConfigModal
        googleCalendar={googleCalendar}
        updateHandler={updateHandler}
        checkLicence={checkLicence}
      />
    ),
  });
};

interface Props {
  googleCalendar: GoogleCalendarDto;
  updateHandler: (patch: Partial<GoogleCalendarDto>) => void;
  checkLicence: (licenceRestriction: LicenceRestriction, featureHandler: () => unknown) => void;
}

const GoogleCalendarConfigModal: FC<Props> = ({ googleCalendar, updateHandler, checkLicence }) => {
  const {
    id,
    backgroundColor,
    calendarName,
    read,
    checkForClashes,
    write,
    recommendation,
    insertOnlySharedRecommendations,
    insertAsFullTimeEvents,
    useCustomStateLabels,
    useEventDateDescription,
  } = googleCalendar;
  const form = useForm({
    initialValues: {
      read,
      checkForClashes,
      write,
      recommendation,
      insertOnlySharedRecommendations,
      insertAsFullTimeEvents,
      useCustomStateLabels,
      useEventDateDescription,
    },
  });
  const [loading, setLoading] = useState(false);

  const { read: readValue, checkForClashes: checkForClashesValue, write: writeValue, recommendation: recommendationValue } = form.values;

  useEffect(() => {
    if (form.isDirty() && readValue && (writeValue || recommendationValue)) {
      form.setValues({ write: false, recommendation: false });
    }

    if (form.isDirty() && !readValue) {
      form.setValues({ checkForClashes: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readValue]);

  useEffect(() => {
    if (form.isDirty() && writeValue && (readValue || checkForClashesValue)) {
      form.setValues({ read: false, checkForClashes: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writeValue]);

  useEffect(() => {
    if (form.isDirty() && checkForClashesValue && !readValue) {
      form.setValues({ read: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForClashesValue]);

  useEffect(() => {
    if (form.isDirty() && recommendationValue) {
      form.setValues({ write: false, read: false, checkForClashes: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendationValue]);

  const onSubmit = () => {
    if (form.isDirty('write') && form.values.write) {
      MixpanelS.track(ConstantS.TrackingEvents.GoogleCalendarActivated);
    }
    setLoading(true);
    GoogleS.editCalendarConfig(id, form.values)
      .then((calendar) => {
        if (calendar) {
          updateHandler(calendar);
          closeAllModals();
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Group>
        <Text>Kalender:</Text>
        <Group mb="md" mt="md">
          <Dot withoutShadow colour={backgroundColor} />
          <Text weight="bolder">{calendarName}</Text>
        </Group>
      </Group>
      <GrayBox>
        <ConfigOption
          title="bookitup Events eintragen"
          description="Es werden automatisch bookitup Termine in Deinen Kalender eingetragen, welche im Status Angefragt, Option oder Bestätigt sind."
          rightSection={<Switch {...form.getInputProps('write', { type: 'checkbox' })} />}
        />
        {writeValue && <ConfigOption
          title="als Ganztags-Event eintragen"
          description="Die Kalender-Einträge werden ganztags eingetragen."
          rightSection={
            <Switch {...form.getInputProps('insertAsFullTimeEvents', {type: 'checkbox'})} disabled={readValue}/>
          }
        />}
        {writeValue && <ConfigOption
          title="Eigenen Buchungsstatus verwenden"
          description="Die Kalender-Einträge enthalten deinen individuell erstellen Buchungsstatus."
          rightSection={
            <Switch {...form.getInputProps('useCustomStateLabels', {type: 'checkbox'})} disabled={readValue}/>
          }
        />}
        {writeValue && <ConfigOption
          title="Event-Tag-Beschreibung verwenden"
          description="Die Kalender-Einträge enthalten die Beschreibung vom Event-Tag."
          rightSection={
            <Switch {...form.getInputProps('useEventDateDescription', {type: 'checkbox'})} disabled={readValue}/>
          }
        />}
        <ConfigOption
          title="Termine anzeigen"
          description="bookitup zeigt Dir in der Kalender-Ansicht die Termine dieses Kalenders an."
          rightSection={
            <Switch
              checked={form.values.read}
              onChange={(e) =>
                checkLicence(LicenceS.Restrictions.PRIVATE_CALENDAR, () =>
                  form.setFieldValue('read', e.currentTarget.checked),
                )
              }
            />
          }
        />
        <ConfigOption
          title="Termine prüfen"
          description="bookitup prüft diesen Kalander auf Terminkollisionen."
          rightSection={
            <Switch
              checked={form.values.checkForClashes}
              disabled={writeValue}
              onChange={(e) =>
                checkLicence(LicenceS.Restrictions.PRIVATE_CALENDAR, () =>
                  form.setFieldValue('checkForClashes', e.currentTarget.checked),
                )
              }
            />
          }
        />
        <ConfigOption
          title="Vermittlungen eintragen"
          description="Es werden automatisch alle Vermittlungen eingetragen, bei denen Du einen anderen Dienstleister vermittelt hast."
          rightSection={<Switch {...form.getInputProps('recommendation', { type: 'checkbox' })} />}
        />
        {recommendationValue && <ConfigOption
          title="Vermittlungen nur eintragen, wenn Auftragsdaten geteilt wurden"
          description="Ist diese Option deaktiviert, werden alle Vermittlungen eingetragen - egal ob die Auftragsdaten geteilt wurden oder nicht."
          rightSection={
            <Switch
              disabled={!form.values.recommendation || readValue}
              {...form.getInputProps('insertOnlySharedRecommendations', {type: 'checkbox'})}
            />
          }
        />}
      </GrayBox>
      <Center mt="xl">
        <Button sx={{ minWidth: 200 }} type="submit" disabled={loading || !form.isTouched()}>
          {i18n._('actions.save')}
        </Button>
      </Center>
    </form>
  );
};
